import { lettersOnlyPattern, namePattern, namePatternPvq, suffixPattern } from 'constants/patterns';
import suffixOptions from 'constants/enums/nameSuffixOptions';
import { nameSuffixes } from 'constants/enums/nameSuffixOptions';
import { ValidateJSModel, NameModel, BranchCollectionModel } from 'forms/shared/types';
import { ForeignContactsItemModel, ForeignContactsItemAliasesModel } from 'forms/sections/foreign/contacts/types';
import { isPVQForm } from 'helpers/form';
import { DeepSafePartial } from 'types/types';

const nameModelValidator: ValidateJSModel<NameModel> = {

  first: (value, attributes, attributeName, options) => {
    const isPVQ = isPVQForm(options?.formType);

    if (attributes.noFirstName) {
      return {
        presence: false,
        length: {
          is: 0,
          message: 'error.name.first.dataIntegrity.checkedWithValue',
        },
      };
    }
    if (isPVQ) {
      if (attributes?.firstLettersOnly) {
        return {
          presence: true,
          length: { minimum: 1, wrongLength: 'error.name.initialonly.length' },
          format: { pattern: lettersOnlyPattern, message: 'error.name.initialonly.pattern' },
        };
      }
    }
    if (isPVQ) {
      return {
        presence: true,
        length: {
          minimum: 2,
          maximum: 100,
          tooShort: 'error.name.first.length.tooshortPvq',
          tooLong: 'error.name.first.length.toolong',
        },
      };
    }
    if (attributes?.firstInitialOnly) {
      return {
        presence: true,
        length: { is: 1, wrongLength: 'error.name.initialonly.length' },
        format: { pattern: lettersOnlyPattern, message: 'error.name.initialonly.pattern' },
      };
    }
    return {
      presence: true,
      format: { pattern: namePattern, message: 'error.name.first.pattern' },
      length: (value, attributes) => ({ // eslint-disable-line no-shadow
        minimum: 2,
        maximum: 100,
        tooShort: 'error.name.first.length.tooshort',
        tooLong: 'error.name.first.length.toolong',
      }),
    };
  },

  firstInitialOnly: {},

  noFirstName: {},

  hideNoFirstName: {},

  last: (value, attributes, attributeName, options) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      if (attributes?.lastLettersOnly) {
        return {
          presence: true,
          length: { minimum: 1, wrongLength: 'error.name.initialonly.length' },
          format: { pattern: lettersOnlyPattern, message: 'error.name.initialonly.pattern' },
        };
      }
    }

    return {
      format: { pattern: namePattern, message: 'error.name.last.pattern' },
      presence: true,
      length: { maximum: 100, tooLong: 'error.name.last.length' },
    };
  },

  middleInitialOnly: {},

  noMiddleName: {},

  hideMiddleName: {},

  middle: (value, attributes, attributeName, options) => {
    const isPVQ = isPVQForm(options?.formType);
    if (options.hideMiddleName || attributes.noMiddleName || attributes.middleUnknown) {
      return {
        presence: false,
        length: {
          is: 0,
          message: 'error.name.middle.dataIntegrity.checkedWithValue',
        },
      };
    }
    if (isPVQ) {
      if (attributes?.middleLettersOnly) {
        return {
          presence: true,
          length: { minimum: 1, wrongLength: 'error.name.initialonly.length' },
          format: { pattern: lettersOnlyPattern, message: 'error.name.initialonly.pattern' },
        };
      }
    }

    if (isPVQ) {
      return {
        presence: true,
        length: {
          minimum: 2,
          maximum: 100,
          tooShort: 'error.name.middle.length.tooshortPvq',
          tooLong: 'error.name.middle.length.toolong',
        },
      };
    }

    if (attributes?.middleInitialOnly) {
      return {
        presence: true,

        length: { is: 1, wrongLength: 'error.name.initialonly.length' },
        format: { pattern: lettersOnlyPattern, message: 'error.name.initialonly.pattern' },
      };
    }

    return {
      presence: true,
      length: {
        minimum: 2,
        maximum: 100,
        tooShort: 'error.name.middle.length.tooshort',
        tooLong: 'error.name.middle.length.toolong',
      },
      format: { pattern: namePattern, message: 'error.name.middle.pattern' },
    };
  },

  suffix: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {
        inclusion: {
          within: nameSuffixes,
          message: 'error.name.suffix.inclusion',
        },
      };
    }

    return {
      inclusion: {
        within: suffixOptions,
        message: 'error.name.suffix.inclusion',
      },
    };
  },

  suffixOther: (value, attributes) => {
    if (attributes.suffix === 'Other') {
      return {
        presence: true,
        format: { pattern: suffixPattern, message: 'error.name.suffixOther.pattern' },
        length: {
          maximum: 100,
          tooLong: 'error.name.suffixOther.length.toolong',
        },
      };
    }

    return {};
  },
};

export default nameModelValidator;

export function createDuplicateForeignContactsAliasNameValidator(
  value: DeepSafePartial<BranchCollectionModel<ForeignContactsItemAliasesModel>> | BranchCollectionModel<ForeignContactsItemAliasesModel>,
  attributes: DeepSafePartial<ForeignContactsItemModel>
) {
  const validator: ValidateJSModel<NameModel> = { ...nameModelValidator };
  const nameMatches = (compare1: NameModel | DeepSafePartial<NameModel> | undefined, compare2: NameModel | DeepSafePartial<NameModel> | undefined) => ( // eslint-disable-line max-len
    compare1 && compare2
      && compare1.first?.toLowerCase() === compare2.first?.toLowerCase()
      && compare1.middle?.toLowerCase() === compare2.middle?.toLowerCase()
      && compare1.last?.toLowerCase() === compare2.last?.toLowerCase()
      && compare1.suffix?.toLowerCase() === compare2.suffix?.toLowerCase()
      && compare1.suffixOther?.toLowerCase() === compare2.suffixOther?.toLowerCase()
      && (compare1.first || compare1.middle || compare1.last)
  );
  const duplicateAliasNames: (NameModel | DeepSafePartial<NameModel> | undefined)[] = [];
  // for each value.items as alias // if Item.Alias {first,middle,last, suffix } exists twice then validate an exclusion error
  if (value?.items) {
    for (const alias of value.items) {
      const sameItems = value.items.filter((x) => nameMatches(alias?.Item?.Alias, x?.Item?.Alias));
      if (sameItems?.length > 1) {
        duplicateAliasNames.push(alias?.Item?.Alias);
      }
    }
  }
  const duplicateError = 'error.name.duplicate.alias';

  validator.first = (value, attributes, attributeName, options) => { // eslint-disable-line no-shadow
    if (duplicateAliasNames.find((x) => nameMatches(attributes, x))) {
      return {
        presence: true,
        exclusion: {
          within: [value],
          message: duplicateError,
        },
      };
    }
    if (attributes.noFirstName || (isPVQForm(options?.formType) && attributes?.firstUnknown === true)) {
      return {
        presence: false,
        length: {
          is: 0,
          message: 'error.name.first.dataIntegrity.checkedWithValue',
        },
      };
    }
    if (isPVQForm(options?.formType)) {
      if (attributes?.firstLettersOnly) {
        return {
          presence: true,
          length: {
            minimum: 1,
            maximum: 100,
            wrongLength: 'error.pvq.name.lettersonly.length'
          },
          format: { pattern: namePatternPvq, message: 'error.pvq.name.lettersonly.pattern' },
        };
      }
    }
    if (attributes.firstInitialOnly) {
      return {
        presence: true,
        length: { is: 1, wrongLength: 'error.name.initialonly.length' },
      };
    }
    return {
      presence: true,
      format: { pattern: namePattern, message: 'error.name.first.pattern' },
      length: (value, attributes) => ({ // eslint-disable-line no-shadow
        minimum: 2,
        maximum: 100,
        tooShort: 'error.name.first.length.tooshort',
        tooLong: 'error.name.first.length.toolong',
      }),
    };
  };

  validator.last = (value, attributes, attributeName, options) => { // eslint-disable-line no-shadow
    if (isPVQForm(options?.formType) && attributes?.lastUnknown === true) {
      return {
        presence: false,
        length: {
          is: 0,
          message: 'error.pvq.name.last.dataIntegrity.checkedWithValue',
        },
      };
    }

    if (duplicateAliasNames.find((x) => nameMatches(attributes, x))) {
      return {
        presence: true,
        exclusion: {
          within: [value],
          message: duplicateError,
        },
      };
    }
    return {
      format: { pattern: namePattern, message: 'error.name.last.pattern' },
      presence: true,
      length: { maximum: 100, tooLong: 'error.name.last.length' },
    };
  };
  validator.middle = (value, attributes, attributeName, options) => { // eslint-disable-line no-shadow
    if (duplicateAliasNames.find((x) => nameMatches(attributes, x))) {
      return {
        presence: true,
        exclusion: {
          within: [value],
          message: duplicateError,
        },
      };
    }
    if (options.hideMiddleName
      || attributes.hideMiddleName
      || attributes.noMiddleName
      || (isPVQForm(options?.formType) && attributes?.middleUnknown === true)) {
      return {
        presence: false,
        length: {
          is: 0,
          message: 'error.name.middle.dataIntegrity.checkedWithValue',
        },
      };
    }

    if (attributes.middleInitialOnly) {
      return {
        presence: true,

        length: { is: 1, wrongLength: 'error.name.initialonly.length' },
        format: { pattern: lettersOnlyPattern, message: 'error.name.initialonly.pattern' },
      };
    }

    return {
      presence: true,
      length: {
        minimum: 2,
        maximum: 100,
        tooShort: 'error.name.middle.length.tooshort',
        tooLong: 'error.name.middle.length.toolong',
      },
      format: { pattern: namePattern, message: 'error.name.middle.pattern' },
    };
  };

  validator.suffix = (value, attributes, attributeName, options) => { // eslint-disable-line no-shadow
    if (duplicateAliasNames.find((x) => nameMatches(attributes, x))) {
      return {
        presence: true,
        exclusion: {
          within: [value],
          message: duplicateError,
        },
      };
    }

    if (isPVQForm(options?.formType)) {
      return {
        inclusion: { within: nameSuffixes, message: 'error.name.suffix.inclusion' },
      };
    }

    return {
      inclusion: { within: suffixOptions, message: 'error.name.suffix.inclusion' },
    };
  };

  validator.suffixOther = (value, attributes) => { // eslint-disable-line no-shadow
    if (duplicateAliasNames.find((x) => nameMatches(attributes, x))) {
      return {
        presence: true,
        exclusion: {
          within: [value],
          message: duplicateError,
        },
      };
    }
    if (attributes.suffix === 'Other') {
      return {
        presence: true,
        format: { pattern: suffixPattern, message: 'error.name.suffixOther.pattern' },
        length: {
          maximum: 100,
          tooLong: 'error.name.suffixOther.length.toolong',
        },
      };
    }

    return {};
  };

  return validator;
}
